<template>

  <div class="w-full h-full flex-col relative ">

    <div class="flex flex-col justify-center h-full max-h-full overflow-hidden w-full">
      <div class="messages-container overflow-y-auto" ref="messagesContainer">


        <the-json-pretty v-if="debug" :the-data="messages" title="Messages"></the-json-pretty>

        <div v-for="message in computedMessages" class="my-3">
          <div v-if="message.role == 'user'">
            <div class="chat chat-start text-left">
              <div class="chat-bubble chat-bubble-tertiary">{{ message.content }}
              </div>
            </div>
          </div>

          <div v-if="message.role == 'assistant'">
            <div class="chat chat-end text-left">
              <div class="chat-image avatar">
                <div class="w-10 rounded-full">
                  <TheLoader v-if="operatorStatus == 'loading'"></TheLoader>
                  <img v-else src="@/assets/img/supapm-logo-square-light.svg">
                </div>
              </div>
              <div class="chat-bubble chat-bubble-secondary" v-html="formatMessage(message.content)">
              </div>
            </div>
          </div>
        </div>

        <div class="chat chat-end text-right" v-if="operatorStatus == 'waiting'">
          <div class="chat-image avatar">
          </div>
          <div class="chat-bubble chat-bubble-primary flex">
            <span class="loading loading-dots loading-md opacity-75"></span>
          </div>
        </div>
      </div>

      <div class="w-full flex justify-center p-1" style="height: 14%;">
        <form @submit.prevent="askQuestion" class="w-full">
          <textarea 
            ref="textareaRef"
            :placeholder="inputPlaceholder"
            class="textarea w-full outline outline-base-300 resize-none overflow-hidden ai-chat-input" 
            v-model="question"
            :disabled="disabled" rows="1" @input="autoGrow" @keydown.enter.prevent="handleEnterPress"
          ></textarea>
          <div class="flex justify-end">
            <button class="btn btn-ghost btn-circle" @click="askQuestion" type="submit" :disabled="question.trim() == ''">
              <img src="@/assets/icons/send-2.svg" alt="Send" width="30px" height="30px" class="icon">
            </button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<style>
/* Add styles for markdown content inside chat bubbles */
.chat-bubble :deep(strong) {
  font-weight: bold;
}

.chat-bubble :deep(p) {
  margin: 0;
}

.chat-bubble :deep(ul),
.chat-bubble :deep(ol) {
  margin: 0;
  padding-left: 1.5em;
}

.messages-container {
  scroll-behavior: smooth;
}
</style>

<script setup>
import { marked } from 'marked'
import { useAiNavigation } from '../composables/useAiNavigation'
import { useAiChat } from '../composables/useAiChat'
import { usePageContext } from '../composables/usePageContext'

const textareaRef = ref(null)

const handleEnterPress = (e) => {
  // Allow new lines with Shift+Enter
  if (e.shiftKey) {
    return
  }
  
  // Only submit if there's content
  if (question.value.trim()) {
    askQuestion()
  }
}

const autoGrow = () => {
  const textarea = textareaRef.value
  if (textarea) {
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 'px'
  }
}

const props = defineProps({
  // Initial messages to populate the chat with
  initialMessages: {
    type: Array,
    default: () => []
  },
  // The company ID to send with each request
  company_id: {
    type: Number,
    default: null
  },
  // The API endpoint to send messages to
  apiEndpoint: {
    type: String,
    default: '/api/agent/chat'
  },
  // Additional data to send with each request
  additionalData: {
    type: Object,
    default: () => ({})
  },
  // Whether to show debug information
  debug: {
    type: Boolean,
    default: false
  },
  // Whether the chat input is disabled
  disabled: {
    type: Boolean,
    default: false
  },
  // Initial message to show when the chat loads
  welcomeMessage: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['message-sent', 'message-received', 'system-command', 'ready', 'company-created', 'project-refreshed'])

const messages = ref([...props.initialMessages])
const question = ref("")
const operatorStatus = ref('loading')
const pageStatus = ref('idle')
const {$supabase} = useNuxtApp()
const route = useRoute()
const { navigateWithCompanyContext } = useAiNavigation()
const messagesContainer = ref(null)
const isOnboarding = ref(false)
const { messageQueue } = useAiChat()
const { getPageContext, getContextDescription } = usePageContext()

const focusInput = () => {
  console.log("focusInput")
  // focus the input field in the ai chat
  textareaRef.value.focus()
}

// expose the focusInput function
defineExpose({
  focusInput
})

const getAnswer = async ({ messages }) => {
  const { data: { session } } = await $supabase.auth.getSession()

  pageStatus.value = 'Getting Answer...'
  
  // Get current page context
  const pageContext = getPageContext()
  const contextDescription = getContextDescription()
  
  const response = await fetch(props.apiEndpoint, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      messages,
      token: session.access_token,
      company_id: props.company_id,
      chat_type: isOnboarding.value ? 'onboarding' : 'general',
      pageContext: pageContext,
      contextDescription: contextDescription,
      ...props.additionalData
    }),
  });

  if (!response.ok) {
    console.log("Stream error:", response)
    throw new Error("Stream errored")
  }
  
  return response.body;
};

const askQuestion = async (addMessage = true) => {
  operatorStatus.value = 'waiting'
  pageStatus.value = 'Asking Question...'

  if (addMessage) {
    const userMessage = {
      role: "user",
      content: question.value
    }
    messages.value.push(userMessage);
    emit('message-sent', userMessage)
    question.value = "";
  }
  try {

    const stream = await getAnswer({ messages: messages.value });
    
    useChatStream({
      stream,
      onChunk: ({ data }) => {
        operatorStatus.value = 'typing'
        
        if (data.includes('__SYSTEM_COMMAND__')) {
          console.log('System command detected in chunk');
          const commandStart = data.indexOf('__SYSTEM_COMMAND__') + '__SYSTEM_COMMAND__'.length;
          const commandEnd = data.indexOf('__END_SYSTEM_COMMAND__');
          const commandString = data.substring(commandStart, commandEnd);
          console.log('Extracted command string:', commandString);
          
          try {
            const commandData = JSON.parse(commandString);
            console.log('Parsed command data:', commandData);
            
            // Handle the system command
            if (commandData._system_command === 'COMPANY_SAVED' && isOnboarding.value) {
              console.log('Company created command received:', commandData);
              // use the company.public_id to navigate to the company page
              navigateWithCompanyContext(`/company/${commandData.public_id}`, {
                query: {
                  onboarding: 'true'
                }
              })
              emit('company-created')
              return;
            }
  
            if (commandData._system_command === 'NAVIGATE') {
              navigateWithCompanyContext(commandData.route)
              return;
            }
            
            if (commandData._system_command === 'REFRESH_PROJECT') {
              console.log('Project refresh command received:', commandData)
              emit('project-refreshed', { project_id: commandData.project_id })
              return;
            }
            
            return;
          } catch (error) {
            console.error('Error parsing system command:', error);
          }
        }
        
        if (!messages.value.length || messages.value[messages.value.length - 1].role !== 'assistant') {
          messages.value.push({
            role: "assistant",
            content: "",
          });
        }
        
        messages.value[messages.value.length - 1].content += data;
        emit('message-received', messages.value[messages.value.length - 1])
        scrollToBottom()
      },
      onReady: () => {
        operatorStatus.value = 'idle';
        pageStatus.value = 'idle';
        emit('ready')
      },
    });

  } catch {
    console.log("Stream errored, adding error message")
    messages.value.push({
      role: "assistant",
      content: "Hmmm something went wrong, please try again",
    });
    operatorStatus.value = 'idle';
    pageStatus.value = 'idle';
    emit('ready')
  }

};

const computedMessages = computed(() => {
  return messages.value.filter(message => message.role !== 'system')
})

const formatMessage = (content) => {
  return marked(content, { sanitize: true });
};

// Add this function to handle scrolling
const scrollToBottom = () => {
  if (messagesContainer.value) {
    nextTick(() => {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    })
  }
}

const simulateStreamResponse = async (content) => {
  const chunks = content.split(' ');
  
  for (const word of chunks) {
    if (!messages.value.length || messages.value[messages.value.length - 1].role !== 'assistant') {
      messages.value.push({
        role: "assistant",
        content: "",
      });
    }
    
    messages.value[messages.value.length - 1].content += word + ' ';
    scrollToBottom()
    await new Promise(resolve => setTimeout(resolve, 50));
  }
  
  operatorStatus.value = 'idle';
};

// Add route watcher to handle navigation
watch(
  () => route.name,
  (newRouteName) => {
    if (newRouteName === 'onboard') {
      // Reset messages and start onboarding
      messages.value = []
      isOnboarding.value = true
      operatorStatus.value = 'typing'
      simulateStreamResponse(
        "Hi! Let's get you started. What's the web address of your company?"
      )
    } else {
      // Reset onboarding state when leaving onboard page
      isOnboarding.value = false
    }
  },
  { immediate: true } // Run immediately to handle initial route
)

// Initialize chat when component is mounted
onMounted(() => {
  console.log("Chat mounted, route query:", route)

  // focus the textarea
  textareaRef.value.focus()
})

// Watch messages for changes (handles user messages)
watch(messages, () => {
  scrollToBottom()
}, { deep: true })

// Watch for new messages in the queue
watch(messageQueue, async (newQueue) => {
  if (newQueue.length > 0) {
    const nextMessage = newQueue[0]
    question.value = nextMessage.message
    if (nextMessage.autoSubmit) {
      await askQuestion()
    }
    messageQueue.value.shift()
  }
}, { deep: true })

// Detect OS for keyboard shortcut display
const isMac = computed(() => {
  if (process.client) {
    return /Mac|iPhone|iPod|iPad/i.test(navigator.platform) || 
           /Mac|iPhone|iPod|iPad/i.test(navigator.userAgent)
  }
  return false
})

const inputPlaceholder = computed(() => {
  if(isOnboarding.value) {
    return 'What\'s the web address of your company?'
  }

  const shortcut = isMac.value ? '⌘⇧L' : 'Ctrl+Shift+L'
  return `Ask anything... (${shortcut})`
})

</script>